import { useState, useEffect } from 'react';
import {
  UserPlusIcon,
  LinkIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  SparklesIcon,
  TrophyIcon,
  GiftIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import referralService, { Referral, ReferralStats } from '../../services/referralService';

export default function ReferralDashboardPage() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCode, setReferralCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const [statsData, referralsData, code] = await Promise.all([
        referralService.getStats(),
        referralService.getReferrals(),
        referralService.getMyReferralCode(),
      ]);

      setStats(statsData);
      setReferrals(referralsData);
      setReferralCode(code);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800';
      case 'QUALIFIED':
        return 'bg-green-100 text-green-800';
      case 'PAID':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">Loading referral dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center">
                <GiftIcon className="h-10 w-10 mr-3" />
                Referral Dashboard
              </h1>
              <p className="text-lg opacity-90">
                Earn rewards by inviting friends to join the platform
              </p>
            </div>
            <TrophyIcon className="h-20 w-20 opacity-20" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <UserPlusIcon className="h-8 w-8 text-purple-600" />
              <span className="text-sm font-medium text-gray-500">Total Referrals</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.total || 0}</div>
            <p className="text-sm text-gray-600 mt-2">
              {stats?.active || 0} active · {stats?.qualified || 0} qualified
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              <span className="text-sm font-medium text-gray-500">Total Commission</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatCurrency(stats?.totalCommission || 0)}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              From {formatCurrency(stats?.totalRevenue || 0)} revenue
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <CheckIcon className="h-8 w-8 text-blue-600" />
              <span className="text-sm font-medium text-gray-500">Paid Out</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.paid || 0}</div>
            <p className="text-sm text-gray-600 mt-2">Successfully paid referrals</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <ChartBarIcon className="h-8 w-8 text-pink-600" />
              <span className="text-sm font-medium text-gray-500">Conversion Rate</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {((stats?.conversionRate || 0) * 100).toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600 mt-2">Qualified conversion</p>
          </div>
        </div>

        {/* Referral Link Section */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-md p-8 mb-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <LinkIcon className="h-12 w-12 text-purple-600 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Referral Link</h2>
              <p className="text-gray-600">
                Share this link to start earning commissions when friends sign up
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Referral Code
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={referralCode}
                      readOnly
                      className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-lg font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(referralCode)}
                      className="ml-3 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                    >
                      {copied ? (
                        <>
                          <CheckIcon className="h-5 w-5 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Referral Link
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(referralLink)}
                    className="ml-3 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors flex items-center"
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="h-5 w-5 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-600 mb-1">10%</div>
                <div className="text-sm text-gray-600">Commission Rate</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-pink-600 mb-1">$5</div>
                <div className="text-sm text-gray-600">Signup Bonus</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600 mb-1">Lifetime</div>
                <div className="text-sm text-gray-600">Commission Duration</div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center">
            <SparklesIcon className="h-7 w-7 mr-2 text-purple-600" />
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Share Your Link</h3>
              <p className="text-sm text-gray-600">
                Share your unique referral link with friends and followers
              </p>
            </div>
            <div className="text-center">
              <div className="bg-pink-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-pink-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">They Sign Up</h3>
              <p className="text-sm text-gray-600">
                When someone signs up using your link, they become your referral
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">They Subscribe</h3>
              <p className="text-sm text-gray-600">
                Your referral becomes a creator or patron on the platform
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">You Earn</h3>
              <p className="text-sm text-gray-600">
                Receive 10% commission on all their transactions
              </p>
            </div>
          </div>
        </div>

        {/* Referrals List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <ArrowTrendingUpIcon className="h-6 w-6 mr-2 text-purple-600" />
              Your Referrals
            </h2>
            <span className="text-sm text-gray-600">{referrals.length} total</span>
          </div>

          {referrals.length === 0 ? (
            <div className="p-12 text-center">
              <UserPlusIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No referrals yet</h3>
              <p className="text-gray-600 mb-6">
                Start sharing your referral link to earn commissions
              </p>
              <button
                onClick={() => copyToClipboard(referralLink)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
              >
                Copy Referral Link
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referred User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signup Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-semibold">
                            {referral.referred?.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {referral.referred?.username || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {referral.referred?.email || ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            referral.status
                          )}`}
                        >
                          {referral.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(referral.signupDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(referral.revenueGenerated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(referral.commission)}
                        <span className="text-xs text-gray-500 ml-1">
                          ({(referral.commissionRate * 100).toFixed(0)}%)
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
